import { useMemo } from "react";

import { Loader } from "@transfr-inc/dashboard-components";
import {
  METRIC_PRESENTATION,
  MetricRegistryProvider,
  OutcomesEmptyState,
  OutcomesEmptyStateKind,
  buildRegistry,
} from "@transfr-inc/dashboard-components/outcome-metrics";

import { services } from "@src/dependencies";
import { useApiRequest } from "@utils/http-client";

/**
 * `notYetSupported` scopes the learner-facing dashboard's MVP, which says nothing
 * about what a CS-authored deck may lead with, so the builder drops the flag and
 * offers every metric the data service serves.
 */
const FULL_TABLE = METRIC_PRESENTATION.map(
  // eslint-disable-next-line no-unused-vars
  ({ notYetSupported, ...entry }) => entry,
);

/**
 * Builds the metric registry from the data service's catalog and publishes it to
 * the report builder.
 *
 * A metric's name comes from the catalog, so there is nothing to render without
 * it: an unreadable or malformed response renders the unavailable state rather
 * than a form whose metrics are unnamed. The contract failure `buildRegistry`
 * throws is caught here rather than left to an error boundary, which would take
 * down the organization page around this tab.
 */
export function ImpactReportMetricRegistry({ children }) {
  const { impactReportService } = services;
  const { loading, response, error } = useApiRequest(() =>
    impactReportService.getMetricCatalog(),
  );

  const registry = useMemo(() => {
    if (!response) {
      return;
    }
    try {
      return buildRegistry(response, { presentation: FULL_TABLE });
    } catch {
      return;
    }
  }, [response]);

  if (loading) {
    return <Loader />;
  }

  if (error || !registry) {
    return (
      <OutcomesEmptyState
        kind={OutcomesEmptyStateKind.UNAVAILABLE}
        level="breakdown"
      />
    );
  }

  return (
    <MetricRegistryProvider registry={registry}>
      {children}
    </MetricRegistryProvider>
  );
}
